import React from "react";
import AdminNavbar from "../Navbars/AdminNavbar.js";
import PortalSidebar from "./PortalSidebar";
import { FaBook, FaCopy, FaChevronDown, FaChevronRight, FaServer, FaRocket } from "react-icons/fa";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useSelector } from 'react-redux';
import configs from "../../configs";

/* ======================================================================
   TYPE DEFINITIONS
   ====================================================================== */

interface Endpoint {
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';
    path: string;
    title: string;
    description: string;
    auth: string;
    headers: { key: string; value: string; required: boolean }[];
    queryParams?: { key: string; type: string; required: boolean; description: string }[];
    body?: string;
    responseCode: number;
    response: string;
    errorCodes?: { code: string; status: number; description: string }[];
}

interface Section {
    id: string;
    title: string;
    description: string;
    baseUrl: string;
    authHeader: string;
    authDescription: string;
    endpoints: Endpoint[];
}

/* ======================================================================
   ENDPOINT DATA — PARTNER API
   ====================================================================== */

const partnerSection: Section = {
    id: 'partner',
    title: 'Partner API',
    description: 'For logistics partners integrating shipment creation, tracking, and wallet management via NIPOST.',
    baseUrl: '/api/v1/partner',
    authHeader: 'X-API-Key',
    authDescription: 'Partner API key (type: partner). Issued on approval.',
    endpoints: [
        {
            method: 'POST',
            path: '/rates/quote',
            title: 'Get Shipping Quote',
            description: 'Get available courier options with pricing and estimated delivery timelines from NIPOST.',
            auth: 'X-API-Key (partner)',
            headers: [
                { key: 'Content-Type', value: 'application/json', required: true },
                { key: 'Accept', value: 'application/json', required: true },
                { key: 'X-API-Key', value: 'sk_live_xxxxxxxxxxxxxxxx', required: true },
            ],
            body: JSON.stringify({
                origin: { name: "Skynet Lagos", email: "ops@skynet.ng", phone: "+2348012345678", address: "12 Allen Avenue, Ikeja", city: "Lagos", state: "Lagos", country: "NG" },
                destination: { name: "Jane Doe", email: "jane@email.com", phone: "+2348098765432", address: "5 Wuse Zone 3", city: "Abuja", state: "FCT", country: "NG" },
                weight: 2.5,
                dimensions: { length: 30, width: 20, height: 15, unit: "cm" },
                items: [{ name: "Electronics", weight: 2.5, quantity: 1, value: 50000 }],
                currency: "NGN"
            }, null, 2),
            responseCode: 200,
            response: JSON.stringify({
                success: true,
                message: "Shipping quote retrieved successfully",
                data: {
                    quotes: [
                        { courier: "NIPOST Standard", fee: 2500.00, currency: "NGN", estimated_days: "3-5", service_type: "standard" },
                        { courier: "NIPOST Express", fee: 4200.00, currency: "NGN", estimated_days: "1-2", service_type: "express" }
                    ]
                }
            }, null, 2),
            errorCodes: [
                { code: "VALIDATION_ERROR", status: 422, description: "Missing or invalid fields (origin, destination, weight)" },
                { code: "NIPOST_ERROR", status: 502, description: "NIPOST API returned an error" },
            ]
        },
        {
            method: 'POST',
            path: '/shipments',
            title: 'Create Shipment',
            description: 'Create a new shipment via NIPOST. Debits partner wallet automatically. Returns NIPOST tracking number.',
            auth: 'X-API-Key (partner)',
            headers: [
                { key: 'Content-Type', value: 'application/json', required: true },
                { key: 'Accept', value: 'application/json', required: true },
                { key: 'X-API-Key', value: 'sk_live_xxxxxxxxxxxxxxxx', required: true },
            ],
            body: JSON.stringify({
                origin: { name: "Skynet Lagos HQ", email: "ops@skynet.ng", phone: "+2348012345678", address: "12 Allen Avenue, Ikeja", city: "Lagos", state: "Lagos", country: "NG" },
                destination: { name: "Jane Doe", email: "jane@email.com", phone: "+2348098765432", address: "5 Wuse Zone 3", city: "Abuja", state: "FCT", country: "NG" },
                weight: 2.5,
                description: "Electronics - Laptop charger",
                items_count: 1,
                service_type: "standard",
                currency: "NGN",
                dimensions: { length: 30, width: 20, height: 15, unit: "cm" },
                items: [{ name: "Laptop Charger", weight: 2.5, quantity: 1, value: 25000 }],
                quoted_fee: 2500.00,
                callback_url: "https://yoursite.com/webhooks/shipment",
                reference: "ORD-2026-001"
            }, null, 2),
            responseCode: 201,
            response: JSON.stringify({
                success: true,
                message: "Shipment created successfully via NIPOST",
                data: {
                    id: 1234,
                    shipment_number: "SKY-20260213-A1B2C3",
                    status: "inReview",
                    payment_status: "paid",
                    service_type: "standard",
                    label_url: null,
                    sender: { name: "Skynet Lagos HQ", phone: "+2348012345678", city: "Lagos", state: "Lagos", country: "NG" },
                    receiver: { name: "Jane Doe", phone: "+2348098765432", city: "Abuja", state: "FCT", country: "NG" },
                    weight: 2.5,
                    total_cost: 2500.00,
                    currency: "NGN",
                    nipost: { tracking_number: "NP-2026-XYZ789", waybill_url: null, status: "pending" },
                    wallet_debit: { amount: 2500.00, new_balance: 47500.00, transaction_id: 567 },
                    created_at: "2026-02-13T12:00:00+01:00"
                }
            }, null, 2),
            errorCodes: [
                { code: "VALIDATION_ERROR", status: 422, description: "Missing required fields" },
                { code: "INSUFFICIENT_BALANCE", status: 402, description: "Wallet balance too low for this shipment" },
                { code: "NIPOST_ERROR", status: 502, description: "NIPOST API failed to create shipment" },
            ]
        },
        {
            method: 'GET',
            path: '/shipments',
            title: 'List Shipments',
            description: 'Retrieve a paginated list of all shipments created by this partner.',
            auth: 'X-API-Key (partner)',
            headers: [
                { key: 'Accept', value: 'application/json', required: true },
                { key: 'X-API-Key', value: 'sk_live_xxxxxxxxxxxxxxxx', required: true },
            ],
            queryParams: [
                { key: 'page', type: 'integer', required: false, description: 'Page number (default: 1)' },
                { key: 'per_page', type: 'integer', required: false, description: 'Results per page (default: 20, max: 100)' },
                { key: 'status', type: 'string', required: false, description: 'Filter by status: inReview, In Transit, Out for Delivery, Proof of Delivery, Cancelled' },
                { key: 'search', type: 'string', required: false, description: 'Search by shipment number, sender or receiver name' },
                { key: 'date_from', type: 'date', required: false, description: 'Filter from date (YYYY-MM-DD)' },
                { key: 'date_to', type: 'date', required: false, description: 'Filter to date (YYYY-MM-DD)' },
            ],
            responseCode: 200,
            response: JSON.stringify({
                success: true,
                message: "Shipments retrieved",
                data: [
                    {
                        id: 1234, shipment_number: "SKY-20260213-A1B2C3", status: "In Transit",
                        payment_status: "paid", service_type: "standard", label_url: "https://nipost.gov.ng/waybill/...",
                        sender: { name: "Skynet Lagos HQ", phone: "+2348012345678", city: "Lagos", state: "Lagos", country: "NG" },
                        receiver: { name: "Jane Doe", phone: "+2348098765432", city: "Abuja", state: "FCT", country: "NG" },
                        weight: 2.5, total_cost: 2500.00, currency: "NGN", created_at: "2026-02-13T12:00:00+01:00"
                    }
                ],
                meta: { current_page: 1, per_page: 20, total: 156, last_page: 8 }
            }, null, 2),
        },
        {
            method: 'GET',
            path: '/shipments/{identifier}',
            title: 'Get Shipment Details',
            description: 'Retrieve a single shipment by Skynet ID, shipment number, or NIPOST tracking number.',
            auth: 'X-API-Key (partner)',
            headers: [
                { key: 'Accept', value: 'application/json', required: true },
                { key: 'X-API-Key', value: 'sk_live_xxxxxxxxxxxxxxxx', required: true },
            ],
            responseCode: 200,
            response: JSON.stringify({
                success: true,
                message: "Shipment retrieved",
                data: {
                    id: 1234, shipment_number: "SKY-20260213-A1B2C3", status: "In Transit",
                    payment_status: "paid", service_type: "standard", label_url: "https://nipost.gov.ng/waybill/...",
                    sender: { name: "Skynet Lagos HQ", phone: "+2348012345678", city: "Lagos", state: "Lagos", country: "NG" },
                    receiver: { name: "Jane Doe", phone: "+2348098765432", city: "Abuja", state: "FCT", country: "NG" },
                    weight: 2.5, total_cost: 2500.00, currency: "NGN", created_at: "2026-02-13T12:00:00+01:00",
                    nipost: { tracking_number: "NP-2026-XYZ789", waybill_url: "https://nipost.gov.ng/waybill/...", status: "in_transit" }
                }
            }, null, 2),
            errorCodes: [
                { code: "NOT_FOUND", status: 404, description: "Shipment not found for this partner" },
            ]
        },
        {
            method: 'GET',
            path: '/shipments/{identifier}/track',
            title: 'Track Shipment',
            description: 'Get real-time tracking information from NIPOST for a shipment.',
            auth: 'X-API-Key (partner)',
            headers: [
                { key: 'Accept', value: 'application/json', required: true },
                { key: 'X-API-Key', value: 'sk_live_xxxxxxxxxxxxxxxx', required: true },
            ],
            responseCode: 200,
            response: JSON.stringify({
                success: true,
                message: "Tracking information retrieved",
                data: {
                    skynet_status: "In Transit",
                    nipost_tracking: {
                        tracking_number: "NP-2026-XYZ789",
                        status: "in_transit",
                        events: [
                            { status: "in_transit", location: "Lagos Sorting Center", timestamp: "2026-02-13T14:30:00Z", description: "Package in transit to destination" },
                            { status: "accepted", location: "Lagos Hub", timestamp: "2026-02-13T10:00:00Z", description: "Package accepted at origin facility" }
                        ]
                    }
                }
            }, null, 2),
            errorCodes: [
                { code: "NOT_FOUND", status: 404, description: "Shipment not found or no NIPOST tracking" },
                { code: "TRACKING_NOT_AVAILABLE", status: 409, description: "Shipment still being processed by NIPOST" },
            ]
        },
        {
            method: 'POST',
            path: '/shipments/{identifier}/waybill',
            title: 'Generate Waybill',
            description: 'Generate or retrieve a printable waybill PDF from NIPOST.',
            auth: 'X-API-Key (partner)',
            headers: [
                { key: 'Accept', value: 'application/json', required: true },
                { key: 'X-API-Key', value: 'sk_live_xxxxxxxxxxxxxxxx', required: true },
            ],
            responseCode: 200,
            response: JSON.stringify({
                success: true,
                message: "Waybill generated successfully",
                data: {
                    download_waybill_url: "https://api-docs.ecommerce.nipost.gov.ng/waybills/NP-2026-XYZ789.pdf",
                    cached: false
                }
            }, null, 2),
            errorCodes: [
                { code: "WAYBILL_NOT_AVAILABLE", status: 409, description: "NIPOST shipment ID not available yet" },
            ]
        },
        {
            method: 'POST',
            path: '/pickups',
            title: 'Schedule Pickup',
            description: 'Schedule a courier pickup for one or more shipments via NIPOST.',
            auth: 'X-API-Key (partner)',
            headers: [
                { key: 'Content-Type', value: 'application/json', required: true },
                { key: 'Accept', value: 'application/json', required: true },
                { key: 'X-API-Key', value: 'sk_live_xxxxxxxxxxxxxxxx', required: true },
            ],
            body: JSON.stringify({
                origin: { name: "Skynet Lagos", phone: "+2348012345678", address: "12 Allen Avenue, Ikeja", city: "Lagos", state: "Lagos", country: "NG" },
                pickup_date: "2026-02-15T09:00:00Z",
                shipment_codes: ["NP-2026-XYZ789", "NP-2026-ABC123"]
            }, null, 2),
            responseCode: 201,
            response: JSON.stringify({
                success: true,
                message: "Pickup scheduled successfully",
                data: { pickup_id: "PU-2026-001", status: "scheduled", pickup_date: "2026-02-15T09:00:00Z", shipments_count: 2 }
            }, null, 2),
        },
        {
            method: 'GET',
            path: '/account/profile',
            title: 'Get Partner Profile',
            description: 'Retrieve the authenticated partner\'s company profile and configuration.',
            auth: 'X-API-Key (partner)',
            headers: [
                { key: 'Accept', value: 'application/json', required: true },
                { key: 'X-API-Key', value: 'sk_live_xxxxxxxxxxxxxxxx', required: true },
            ],
            responseCode: 200,
            response: JSON.stringify({
                success: true, message: "Profile retrieved",
                data: {
                    id: 42, company_name: "FastLogistics Ltd", contact_email: "admin@fastlogistics.ng",
                    contact_phone: "+2348012345678", status: "active", wallet_balance: 50000.00,
                    rate_limit_per_minute: 120, created_at: "2026-01-15T08:00:00+01:00"
                }
            }, null, 2),
        },
        {
            method: 'GET',
            path: '/account/balance',
            title: 'Get Wallet Balance',
            description: 'Check the current wallet balance for this partner.',
            auth: 'X-API-Key (partner)',
            headers: [
                { key: 'Accept', value: 'application/json', required: true },
                { key: 'X-API-Key', value: 'sk_live_xxxxxxxxxxxxxxxx', required: true },
            ],
            responseCode: 200,
            response: JSON.stringify({
                success: true, message: "Balance retrieved",
                data: { balance: 50000.00, currency: "NGN" }
            }, null, 2),
        },
        {
            method: 'GET',
            path: '/account/transactions',
            title: 'List Wallet Transactions',
            description: 'List credit and debit transactions on the partner wallet.',
            auth: 'X-API-Key (partner)',
            headers: [
                { key: 'Accept', value: 'application/json', required: true },
                { key: 'X-API-Key', value: 'sk_live_xxxxxxxxxxxxxxxx', required: true },
            ],
            queryParams: [
                { key: 'page', type: 'integer', required: false, description: 'Page number' },
                { key: 'per_page', type: 'integer', required: false, description: 'Results per page (max 100)' },
            ],
            responseCode: 200,
            response: JSON.stringify({
                success: true, message: "Transactions retrieved",
                data: [
                    { id: 567, type: "debit", amount: 2500.00, balance_after: 47500.00, description: "Shipment SKY-20260213-A1B2C3", reference: "SHP-1234", created_at: "2026-02-13T12:00:00+01:00" },
                    { id: 566, type: "credit", amount: 50000.00, balance_after: 50000.00, description: "Admin wallet top-up", reference: "ADM-001", created_at: "2026-02-12T09:00:00+01:00" }
                ],
                meta: { current_page: 1, per_page: 20, total: 45, last_page: 3 }
            }, null, 2),
        },
        {
            method: 'GET',
            path: '/webhooks',
            title: 'List Webhooks',
            description: 'List all webhook subscriptions configured for this partner.',
            auth: 'X-API-Key (partner)',
            headers: [
                { key: 'Accept', value: 'application/json', required: true },
                { key: 'X-API-Key', value: 'sk_live_xxxxxxxxxxxxxxxx', required: true },
            ],
            responseCode: 200,
            response: JSON.stringify({
                success: true, message: "Webhooks retrieved",
                data: [
                    { id: 10, url: "https://yoursite.com/webhooks/skynet", events: ["shipment.created", "shipment.status_changed"], is_active: true, consecutive_failures: 0, created_at: "2026-01-20T10:00:00+01:00" }
                ]
            }, null, 2),
        },
        {
            method: 'POST',
            path: '/webhooks',
            title: 'Create Webhook',
            description: 'Register a new webhook URL to receive event notifications.',
            auth: 'X-API-Key (partner)',
            headers: [
                { key: 'Content-Type', value: 'application/json', required: true },
                { key: 'Accept', value: 'application/json', required: true },
                { key: 'X-API-Key', value: 'sk_live_xxxxxxxxxxxxxxxx', required: true },
            ],
            body: JSON.stringify({
                url: "https://yoursite.com/webhooks/skynet",
                events: ["shipment.created", "shipment.status_changed", "shipment.delivered"],
                secret: "whsec_your_signing_secret_here"
            }, null, 2),
            responseCode: 201,
            response: JSON.stringify({
                success: true, message: "Webhook created",
                data: { id: 11, url: "https://yoursite.com/webhooks/skynet", events: ["shipment.created", "shipment.status_changed", "shipment.delivered"], is_active: true, created_at: "2026-02-13T12:00:00+01:00" }
            }, null, 2),
        },
        {
            method: 'PUT',
            path: '/webhooks/{id}',
            title: 'Update Webhook',
            description: 'Update an existing webhook URL, events, or active status.',
            auth: 'X-API-Key (partner)',
            headers: [
                { key: 'Content-Type', value: 'application/json', required: true },
                { key: 'Accept', value: 'application/json', required: true },
                { key: 'X-API-Key', value: 'sk_live_xxxxxxxxxxxxxxxx', required: true },
            ],
            body: JSON.stringify({
                url: "https://yoursite.com/webhooks/skynet-v2",
                events: ["shipment.created", "shipment.status_changed", "shipment.delivered", "shipment.cancelled"],
                is_active: true
            }, null, 2),
            responseCode: 200,
            response: JSON.stringify({
                success: true, message: "Webhook updated",
                data: { id: 11, url: "https://yoursite.com/webhooks/skynet-v2", events: ["shipment.created", "shipment.status_changed", "shipment.delivered", "shipment.cancelled"], is_active: true }
            }, null, 2),
        },
        {
            method: 'DELETE',
            path: '/webhooks/{id}',
            title: 'Delete Webhook',
            description: 'Remove a webhook subscription.',
            auth: 'X-API-Key (partner)',
            headers: [
                { key: 'Accept', value: 'application/json', required: true },
                { key: 'X-API-Key', value: 'sk_live_xxxxxxxxxxxxxxxx', required: true },
            ],
            responseCode: 200,
            response: JSON.stringify({ success: true, message: "Webhook deleted" }, null, 2),
        },
        {
            method: 'POST',
            path: '/webhooks/{id}/test',
            title: 'Test Webhook',
            description: 'Send a test event payload to the webhook URL to verify it is reachable.',
            auth: 'X-API-Key (partner)',
            headers: [
                { key: 'Accept', value: 'application/json', required: true },
                { key: 'X-API-Key', value: 'sk_live_xxxxxxxxxxxxxxxx', required: true },
            ],
            responseCode: 200,
            response: JSON.stringify({
                success: true, message: "Test webhook delivered",
                data: { status_code: 200, response_time_ms: 145, delivered: true }
            }, null, 2),
        },
    ],
};

/* ======================================================================
   ENDPOINT DATA — CUSTOMER API
   ====================================================================== */

const customerSection: Section = {
    id: 'customer',
    title: 'Customer API',
    description: 'For Skynet registered customers to manage their shipments, track packages, and view analytics.',
    baseUrl: '/api/v1/customer',
    authHeader: 'X-API-Key',
    authDescription: 'Customer API key (type: customer). Request via admin approval.',
    endpoints: [
        {
            method: 'POST',
            path: '/rates/calculate',
            title: 'Calculate Shipping Rate',
            description: 'Calculate shipping cost using Skynet\'s internal rate card (not NIPOST). Returns pricing based on weight, origin, and destination.',
            auth: 'X-API-Key (customer)',
            headers: [
                { key: 'Content-Type', value: 'application/json', required: true },
                { key: 'Accept', value: 'application/json', required: true },
                { key: 'X-API-Key', value: 'sk_cust_xxxxxxxxxxxxxxxx', required: true },
            ],
            body: JSON.stringify({
                origin_state: "Lagos",
                origin_city: "Lagos",
                destination_state: "FCT",
                destination_city: "Abuja",
                weight: 5.0,
                service_type: "standard"
            }, null, 2),
            responseCode: 200,
            response: JSON.stringify({
                success: true, message: "Rate calculated",
                data: { cost: 3500.00, currency: "NGN", service_type: "standard", estimated_days: "3-5" }
            }, null, 2),
        },
        {
            method: 'POST',
            path: '/shipments',
            title: 'Create Shipment',
            description: 'Create a shipment using the Skynet platform. Uses the customer\'s registered address as sender.',
            auth: 'X-API-Key (customer)',
            headers: [
                { key: 'Content-Type', value: 'application/json', required: true },
                { key: 'Accept', value: 'application/json', required: true },
                { key: 'X-API-Key', value: 'sk_cust_xxxxxxxxxxxxxxxx', required: true },
            ],
            body: JSON.stringify({
                receiver_name: "Jane Doe",
                receiver_phone: "+2348098765432",
                receiver_email: "jane@email.com",
                receiver_address: "5 Wuse Zone 3",
                receiver_city: "Abuja",
                receiver_state: "FCT",
                weight: 2.5,
                description: "Documents",
                declared_value: 10000,
                service_type: "express"
            }, null, 2),
            responseCode: 201,
            response: JSON.stringify({
                success: true, message: "Shipment created successfully",
                data: {
                    id: 5678, shipment_number: "SKY-20260213-D4E5F6", status: "inReview",
                    total_cost: 4200.00, currency: "NGN", created_at: "2026-02-13T14:00:00+01:00"
                }
            }, null, 2),
        },
        {
            method: 'POST',
            path: '/shipments/bulk',
            title: 'Bulk Create Shipments',
            description: 'Create multiple shipments in a single request (max 50 per batch).',
            auth: 'X-API-Key (customer)',
            headers: [
                { key: 'Content-Type', value: 'application/json', required: true },
                { key: 'Accept', value: 'application/json', required: true },
                { key: 'X-API-Key', value: 'sk_cust_xxxxxxxxxxxxxxxx', required: true },
            ],
            body: JSON.stringify({
                shipments: [
                    { receiver_name: "Jane Doe", receiver_phone: "+2348098765432", receiver_address: "5 Wuse Zone 3", receiver_city: "Abuja", receiver_state: "FCT", weight: 2.0, description: "Package 1" },
                    { receiver_name: "John Smith", receiver_phone: "+2348011111111", receiver_address: "10 Ring Road", receiver_city: "Ibadan", receiver_state: "Oyo", weight: 3.5, description: "Package 2" }
                ]
            }, null, 2),
            responseCode: 201,
            response: JSON.stringify({
                success: true, message: "2 of 2 shipments created",
                data: { created: 2, failed: 0, shipments: [{ id: 5679, shipment_number: "SKY-20260213-G7H8I9", status: "inReview" }, { id: 5680, shipment_number: "SKY-20260213-J0K1L2", status: "inReview" }] }
            }, null, 2),
        },
        {
            method: 'GET',
            path: '/shipments',
            title: 'List Shipments',
            description: 'Retrieve paginated list of shipments for the authenticated customer.',
            auth: 'X-API-Key (customer)',
            headers: [
                { key: 'Accept', value: 'application/json', required: true },
                { key: 'X-API-Key', value: 'sk_cust_xxxxxxxxxxxxxxxx', required: true },
            ],
            queryParams: [
                { key: 'page', type: 'integer', required: false, description: 'Page number (default: 1)' },
                { key: 'per_page', type: 'integer', required: false, description: 'Results per page (default: 20, max: 100)' },
                { key: 'status', type: 'string', required: false, description: 'Filter by status' },
                { key: 'search', type: 'string', required: false, description: 'Search by shipment number or name' },
                { key: 'date_from', type: 'date', required: false, description: 'Start date (YYYY-MM-DD)' },
                { key: 'date_to', type: 'date', required: false, description: 'End date (YYYY-MM-DD)' },
            ],
            responseCode: 200,
            response: JSON.stringify({
                success: true, message: "Shipments retrieved",
                data: [{ id: 5678, shipment_number: "SKY-20260213-D4E5F6", status: "In Transit", receiver_name: "Jane Doe", weight: 2.5, total_cost: 4200.00, created_at: "2026-02-13T14:00:00+01:00" }],
                meta: { current_page: 1, per_page: 20, total: 89, last_page: 5 }
            }, null, 2),
        },
        {
            method: 'GET',
            path: '/shipments/{shipmentNumber}',
            title: 'Get Shipment Details',
            description: 'Get full details of a specific shipment by its shipment number.',
            auth: 'X-API-Key (customer)',
            headers: [
                { key: 'Accept', value: 'application/json', required: true },
                { key: 'X-API-Key', value: 'sk_cust_xxxxxxxxxxxxxxxx', required: true },
            ],
            responseCode: 200,
            response: JSON.stringify({
                success: true, message: "Shipment retrieved",
                data: { id: 5678, shipment_number: "SKY-20260213-D4E5F6", status: "In Transit", payment_status: "paid", sender: { name: "My Company", phone: "+2348012345678", city: "Lagos", state: "Lagos" }, receiver: { name: "Jane Doe", phone: "+2348098765432", city: "Abuja", state: "FCT" }, weight: 2.5, total_cost: 4200.00, currency: "NGN", created_at: "2026-02-13T14:00:00+01:00" }
            }, null, 2),
        },
        {
            method: 'GET',
            path: '/shipments/{shipmentNumber}/track',
            title: 'Track Shipment',
            description: 'Get tracking events and current status for a shipment.',
            auth: 'X-API-Key (customer)',
            headers: [
                { key: 'Accept', value: 'application/json', required: true },
                { key: 'X-API-Key', value: 'sk_cust_xxxxxxxxxxxxxxxx', required: true },
            ],
            responseCode: 200,
            response: JSON.stringify({
                success: true, message: "Tracking retrieved",
                data: { shipment_number: "SKY-20260213-D4E5F6", status: "In Transit", tracking_events: [{ status: "In Transit", location: "Lagos Hub", timestamp: "2026-02-13T15:00:00Z", description: "Package departed origin" }, { status: "inReview", location: "Lagos", timestamp: "2026-02-13T14:00:00Z", description: "Shipment created" }] }
            }, null, 2),
        },
        {
            method: 'POST',
            path: '/shipments/{shipmentNumber}/waybill',
            title: 'Generate Waybill',
            description: 'Generate a printable shipping waybill/label PDF.',
            auth: 'X-API-Key (customer)',
            headers: [
                { key: 'Accept', value: 'application/json', required: true },
                { key: 'X-API-Key', value: 'sk_cust_xxxxxxxxxxxxxxxx', required: true },
            ],
            responseCode: 200,
            response: JSON.stringify({
                success: true, message: "Waybill generated",
                data: { download_url: "https://api.skynet.ng/waybills/SKY-20260213-D4E5F6.pdf" }
            }, null, 2),
        },
        {
            method: 'DELETE',
            path: '/shipments/{shipmentNumber}',
            title: 'Cancel Shipment',
            description: 'Cancel a shipment. Only shipments in "inReview" status can be cancelled.',
            auth: 'X-API-Key (customer)',
            headers: [
                { key: 'Accept', value: 'application/json', required: true },
                { key: 'X-API-Key', value: 'sk_cust_xxxxxxxxxxxxxxxx', required: true },
            ],
            responseCode: 200,
            response: JSON.stringify({ success: true, message: "Shipment cancelled successfully" }, null, 2),
            errorCodes: [
                { code: "CANNOT_CANCEL", status: 409, description: "Shipment already in transit or delivered" },
            ]
        },
        {
            method: 'GET',
            path: '/account/profile',
            title: 'Get Customer Profile',
            description: 'Retrieve authenticated customer profile information.',
            auth: 'X-API-Key (customer)',
            headers: [
                { key: 'Accept', value: 'application/json', required: true },
                { key: 'X-API-Key', value: 'sk_cust_xxxxxxxxxxxxxxxx', required: true },
            ],
            responseCode: 200,
            response: JSON.stringify({
                success: true, message: "Profile retrieved",
                data: { id: 99, name: "Acme Corp", email: "admin@acme.ng", phone: "+2348012345678", address: "10 Marina, Lagos Island", city: "Lagos", state: "Lagos", api_access: true }
            }, null, 2),
        },
        {
            method: 'GET',
            path: '/account/balance',
            title: 'Get Account Balance',
            description: 'Check the customer wallet/credit balance.',
            auth: 'X-API-Key (customer)',
            headers: [
                { key: 'Accept', value: 'application/json', required: true },
                { key: 'X-API-Key', value: 'sk_cust_xxxxxxxxxxxxxxxx', required: true },
            ],
            responseCode: 200,
            response: JSON.stringify({
                success: true, message: "Balance retrieved",
                data: { balance: 125000.00, currency: "NGN" }
            }, null, 2),
        },
        {
            method: 'GET',
            path: '/account/transactions',
            title: 'List Account Transactions',
            description: 'List all credit/debit transactions on the customer account.',
            auth: 'X-API-Key (customer)',
            headers: [
                { key: 'Accept', value: 'application/json', required: true },
                { key: 'X-API-Key', value: 'sk_cust_xxxxxxxxxxxxxxxx', required: true },
            ],
            responseCode: 200,
            response: JSON.stringify({
                success: true, message: "Transactions retrieved",
                data: [{ id: 200, type: "debit", amount: 4200.00, description: "Shipment SKY-20260213-D4E5F6", created_at: "2026-02-13T14:00:00+01:00" }],
                meta: { current_page: 1, per_page: 20, total: 32, last_page: 2 }
            }, null, 2),
        },
        {
            method: 'GET',
            path: '/analytics/dashboard',
            title: 'Get Analytics Dashboard',
            description: 'Retrieve shipment analytics: totals, status breakdown, spending trends.',
            auth: 'X-API-Key (customer)',
            headers: [
                { key: 'Accept', value: 'application/json', required: true },
                { key: 'X-API-Key', value: 'sk_cust_xxxxxxxxxxxxxxxx', required: true },
            ],
            responseCode: 200,
            response: JSON.stringify({
                success: true, message: "Dashboard retrieved",
                data: { total_shipments: 89, in_transit: 12, delivered: 65, cancelled: 3, total_spent: 285000.00, currency: "NGN" }
            }, null, 2),
        },
        {
            method: 'GET',
            path: '/templates/shipment',
            title: 'Download Shipment Template',
            description: 'Download an Excel template for bulk shipment uploads.',
            auth: 'X-API-Key (customer)',
            headers: [
                { key: 'X-API-Key', value: 'sk_cust_xxxxxxxxxxxxxxxx', required: true },
            ],
            responseCode: 200,
            response: '// Returns an .xlsx file download\n// Content-Type: application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        {
            method: 'POST',
            path: '/webhooks',
            title: 'Create Webhook',
            description: 'Register a webhook URL to receive event notifications for this customer.',
            auth: 'X-API-Key (customer)',
            headers: [
                { key: 'Content-Type', value: 'application/json', required: true },
                { key: 'Accept', value: 'application/json', required: true },
                { key: 'X-API-Key', value: 'sk_cust_xxxxxxxxxxxxxxxx', required: true },
            ],
            body: JSON.stringify({
                url: "https://yoursite.com/webhooks/skynet",
                events: ["shipment.created", "shipment.status_changed", "shipment.delivered"],
                secret: "whsec_your_signing_secret"
            }, null, 2),
            responseCode: 201,
            response: JSON.stringify({
                success: true, message: "Webhook created",
                data: { id: 15, url: "https://yoursite.com/webhooks/skynet", events: ["shipment.created", "shipment.status_changed", "shipment.delivered"], is_active: true }
            }, null, 2),
        },
    ],
};

/* ======================================================================
   ENDPOINT DATA — ADMIN API
   ====================================================================== */

const adminSection: Section = {
    id: 'admin',
    title: 'Admin API',
    description: 'Internal administration endpoints for managing partners, API keys, analytics, portal accounts, and system health.',
    baseUrl: '/api/v1/admin',
    authHeader: 'X-Admin-Token',
    authDescription: 'Static admin token from server .env (API_ADMIN_TOKEN). Or X-API-Key from an admin user.',
    endpoints: [
        // --- Partners ---
        {
            method: 'GET', path: '/partners', title: 'List All Partners',
            description: 'Retrieve paginated list of all API partners with summary counts.',
            auth: 'X-Admin-Token',
            headers: [{ key: 'Accept', value: 'application/json', required: true }, { key: 'X-Admin-Token', value: 'your_admin_token', required: true }],
            queryParams: [
                { key: 'page', type: 'integer', required: false, description: 'Page number' },
                { key: 'per_page', type: 'integer', required: false, description: 'Results per page' },
                { key: 'search', type: 'string', required: false, description: 'Search by company name or email' },
                { key: 'status', type: 'string', required: false, description: 'Filter: active, pending, suspended, rejected' },
            ],
            responseCode: 200,
            response: JSON.stringify({
                success: true, data: [{ id: 42, company_name: "FastLogistics Ltd", contact_email: "admin@fastlogistics.ng", status: "active", wallet_balance: 50000.00 }],
                summary: { total: 25, active: 18, pending: 5, suspended: 2 }, meta: { current_page: 1, last_page: 2, total: 25 }
            }, null, 2),
        },
        {
            method: 'POST', path: '/partners', title: 'Create Partner',
            description: 'Manually create a new partner (without portal registration).',
            auth: 'X-Admin-Token',
            headers: [{ key: 'Content-Type', value: 'application/json', required: true }, { key: 'X-Admin-Token', value: 'your_admin_token', required: true }],
            body: JSON.stringify({ company_name: "New Courier Ltd", contact_email: "hello@newcourier.ng", rate_limit_per_minute: 120 }, null, 2),
            responseCode: 201,
            response: JSON.stringify({ success: true, message: "Partner created successfully", data: { id: 43, company_name: "New Courier Ltd", status: "pending" } }, null, 2),
        },
        {
            method: 'GET', path: '/partners/{id}', title: 'Get Partner Details',
            description: 'Retrieve full details for a single partner including API keys, webhooks, and transaction history.',
            auth: 'X-Admin-Token',
            headers: [{ key: 'Accept', value: 'application/json', required: true }, { key: 'X-Admin-Token', value: 'your_admin_token', required: true }],
            responseCode: 200,
            response: JSON.stringify({ success: true, data: { id: 42, company_name: "FastLogistics Ltd", contact_email: "admin@fastlogistics.ng", contact_phone: "+2348012345678", status: "active", wallet_balance: 50000.00, api_keys: [{ id: 1, key_prefix: "sk_live_abc", is_active: true, total_requests: 15420 }], recent_transactions: [], shipment_stats: { total: 340, this_month: 28 } } }, null, 2),
        },
        {
            method: 'PUT', path: '/partners/{id}', title: 'Update Partner',
            description: 'Update partner company name, contact details, or rate limit.',
            auth: 'X-Admin-Token',
            headers: [{ key: 'Content-Type', value: 'application/json', required: true }, { key: 'X-Admin-Token', value: 'your_admin_token', required: true }],
            body: JSON.stringify({ company_name: "FastLogistics International", contact_email: "new@fastlogistics.ng", rate_limit_per_minute: 200 }, null, 2),
            responseCode: 200,
            response: JSON.stringify({ success: true, message: "Partner updated", data: { id: 42, company_name: "FastLogistics International" } }, null, 2),
        },
        {
            method: 'POST', path: '/partners/{id}/approve', title: 'Approve Partner',
            description: 'Approve a pending partner and optionally generate an API key.',
            auth: 'X-Admin-Token',
            headers: [{ key: 'Content-Type', value: 'application/json', required: true }, { key: 'X-Admin-Token', value: 'your_admin_token', required: true }],
            body: JSON.stringify({ generate_api_key: true }, null, 2),
            responseCode: 200,
            response: JSON.stringify({ success: true, message: "Partner approved", data: { id: 42, status: "active", api_key: "sk_live_abc123..." } }, null, 2),
        },
        {
            method: 'POST', path: '/partners/{id}/reject', title: 'Reject Partner', description: 'Reject a pending partner with a reason.', auth: 'X-Admin-Token',
            headers: [{ key: 'Content-Type', value: 'application/json', required: true }, { key: 'X-Admin-Token', value: 'your_admin_token', required: true }],
            body: JSON.stringify({ reason: "Incomplete business documentation" }, null, 2),
            responseCode: 200, response: JSON.stringify({ success: true, message: "Partner rejected" }, null, 2),
        },
        {
            method: 'POST', path: '/partners/{id}/suspend', title: 'Suspend Partner', description: 'Suspend an active partner. All API keys become inactive.', auth: 'X-Admin-Token',
            headers: [{ key: 'Content-Type', value: 'application/json', required: true }, { key: 'X-Admin-Token', value: 'your_admin_token', required: true }],
            body: JSON.stringify({ reason: "Terms of service violation" }, null, 2),
            responseCode: 200, response: JSON.stringify({ success: true, message: "Partner suspended" }, null, 2),
        },
        {
            method: 'POST', path: '/partners/{id}/reactivate', title: 'Reactivate Partner', description: 'Reactivate a suspended partner.', auth: 'X-Admin-Token',
            headers: [{ key: 'X-Admin-Token', value: 'your_admin_token', required: true }],
            responseCode: 200, response: JSON.stringify({ success: true, message: "Partner reactivated" }, null, 2),
        },
        {
            method: 'POST', path: '/partners/{id}/wallet/credit', title: 'Credit Partner Wallet', description: 'Add funds to a partner\'s wallet.', auth: 'X-Admin-Token',
            headers: [{ key: 'Content-Type', value: 'application/json', required: true }, { key: 'X-Admin-Token', value: 'your_admin_token', required: true }],
            body: JSON.stringify({ amount: 50000, description: "Monthly credit top-up", reference: "ADM-CR-001" }, null, 2),
            responseCode: 200, response: JSON.stringify({ success: true, message: "Wallet credited", data: { new_balance: 100000.00, transaction_id: 789 } }, null, 2),
        },
        {
            method: 'POST', path: '/partners/{id}/wallet/debit', title: 'Debit Partner Wallet', description: 'Deduct funds from a partner\'s wallet.', auth: 'X-Admin-Token',
            headers: [{ key: 'Content-Type', value: 'application/json', required: true }, { key: 'X-Admin-Token', value: 'your_admin_token', required: true }],
            body: JSON.stringify({ amount: 5000, description: "Service charge", reference: "ADM-DB-001" }, null, 2),
            responseCode: 200, response: JSON.stringify({ success: true, message: "Wallet debited", data: { new_balance: 95000.00, transaction_id: 790 } }, null, 2),
        },
        // --- API Keys ---
        {
            method: 'GET', path: '/api-keys', title: 'List All API Keys', description: 'List all API keys across all partners and customers.', auth: 'X-Admin-Token',
            headers: [{ key: 'X-Admin-Token', value: 'your_admin_token', required: true }],
            queryParams: [{ key: 'page', type: 'integer', required: false, description: 'Page number' }, { key: 'status', type: 'string', required: false, description: 'active or revoked' }],
            responseCode: 200,
            response: JSON.stringify({ success: true, data: [{ id: 1, key_prefix: "sk_live_abc", type: "partner", keyable_type: "Partner", keyable_id: 42, is_active: true, total_requests: 15420, last_used_at: "2026-02-13T12:00:00Z" }], meta: { total: 45 } }, null, 2),
        },
        {
            method: 'POST', path: '/api-keys', title: 'Create API Key', description: 'Generate a new API key for a partner or customer.', auth: 'X-Admin-Token',
            headers: [{ key: 'Content-Type', value: 'application/json', required: true }, { key: 'X-Admin-Token', value: 'your_admin_token', required: true }],
            body: JSON.stringify({ owner_type: "partner", owner_id: 42, type: "live", permissions: ["shipments.create", "shipments.read", "rates.calculate"] }, null, 2),
            responseCode: 201,
            response: JSON.stringify({ success: true, message: "API key created", data: { id: 50, key: "sk_live_NEW_KEY_SHOWN_ONCE", key_prefix: "sk_live_NEW", is_active: true } }, null, 2),
        },
        {
            method: 'POST', path: '/api-keys/{id}/revoke', title: 'Revoke API Key', description: 'Permanently deactivate an API key.', auth: 'X-Admin-Token',
            headers: [{ key: 'X-Admin-Token', value: 'your_admin_token', required: true }],
            responseCode: 200, response: JSON.stringify({ success: true, message: "API key revoked" }, null, 2),
        },
        {
            method: 'POST', path: '/api-keys/{id}/rotate', title: 'Rotate API Key', description: 'Generate a new key value, invalidating the old one.', auth: 'X-Admin-Token',
            headers: [{ key: 'X-Admin-Token', value: 'your_admin_token', required: true }],
            responseCode: 200,
            response: JSON.stringify({ success: true, message: "API key rotated", data: { id: 1, new_key: "sk_live_ROTATED_KEY_SHOWN_ONCE", key_prefix: "sk_live_ROT" } }, null, 2),
        },
        // --- Portal Accounts ---
        {
            method: 'GET', path: '/portal-accounts', title: 'List Portal Accounts', description: 'List self-service portal registrations (pending, active, rejected).', auth: 'X-Admin-Token',
            headers: [{ key: 'X-Admin-Token', value: 'your_admin_token', required: true }],
            queryParams: [{ key: 'status', type: 'string', required: false, description: 'pending, active, rejected' }, { key: 'account_type', type: 'string', required: false, description: 'partner or customer' }],
            responseCode: 200,
            response: JSON.stringify({ success: true, data: { data: [{ id: 1, company_name: "NewCo", contact_name: "John", contact_email: "john@newco.ng", account_type: "partner", status: "pending", created_at: "2026-02-13T10:00:00Z" }], current_page: 1, total: 8 } }, null, 2),
        },
        {
            method: 'POST', path: '/portal-accounts/{id}/approve', title: 'Approve Portal Account', description: 'Approve a portal registration. Creates a Partner, generates API key, and emails credentials.', auth: 'X-Admin-Token',
            headers: [{ key: 'Content-Type', value: 'application/json', required: true }, { key: 'X-Admin-Token', value: 'your_admin_token', required: true }],
            body: JSON.stringify({ rate_limit_per_minute: 120 }, null, 2),
            responseCode: 200,
            response: JSON.stringify({ success: true, message: "Account approved", data: { partner_id: 44, api_key: "sk_live_GENERATED_KEY", api_secret: "sk_secret_GENERATED" } }, null, 2),
        },
        {
            method: 'POST', path: '/portal-accounts/{id}/reject', title: 'Reject Portal Account', description: 'Reject a portal registration with a reason (emailed to applicant).', auth: 'X-Admin-Token',
            headers: [{ key: 'Content-Type', value: 'application/json', required: true }, { key: 'X-Admin-Token', value: 'your_admin_token', required: true }],
            body: JSON.stringify({ reason: "Incomplete business information" }, null, 2),
            responseCode: 200,
            response: JSON.stringify({ success: true, message: "Account rejected" }, null, 2),
        },
        // --- Analytics ---
        {
            method: 'GET', path: '/analytics/overview', title: 'Analytics Overview', description: 'Get system-wide API analytics: request counts, partner stats, shipment totals.', auth: 'X-Admin-Token',
            headers: [{ key: 'X-Admin-Token', value: 'your_admin_token', required: true }],
            queryParams: [{ key: 'period', type: 'integer', required: false, description: 'Days to look back (7, 30, 90). Default: 30' }],
            responseCode: 200,
            response: JSON.stringify({ success: true, data: { api_requests: { total: 45230, errors: 312, avg_response_time: 145 }, partners: { total: 25, active: 18, pending: 5 }, api_keys: { total: 45, active: 38 }, shipments: { total: 2340, from_api: 890 } } }, null, 2),
        },
        {
            method: 'GET', path: '/analytics/requests', title: 'API Request Analytics', description: 'Detailed API request metrics with time-series data.', auth: 'X-Admin-Token',
            headers: [{ key: 'X-Admin-Token', value: 'your_admin_token', required: true }],
            queryParams: [{ key: 'period', type: 'integer', required: false, description: 'Days (default: 30)' }, { key: 'source', type: 'string', required: false, description: 'Filter by source: partner, customer' }],
            responseCode: 200,
            response: JSON.stringify({ success: true, data: { total: 45230, by_day: [{ date: "2026-02-13", count: 1520, errors: 12 }], by_endpoint: [{ endpoint: "/partner/shipments", count: 8900, avg_ms: 230 }] } }, null, 2),
        },
        {
            method: 'GET', path: '/analytics/top-consumers', title: 'Top API Consumers', description: 'Ranked list of partners/customers by API usage.', auth: 'X-Admin-Token',
            headers: [{ key: 'X-Admin-Token', value: 'your_admin_token', required: true }],
            queryParams: [{ key: 'period', type: 'integer', required: false, description: 'Days (default: 30)' }, { key: 'limit', type: 'integer', required: false, description: 'Max results (default: 10)' }],
            responseCode: 200,
            response: JSON.stringify({ success: true, data: [{ rank: 1, owner: "FastLogistics Ltd", owner_type: "Partner", total_requests: 8900, error_rate: "0.8%" }] }, null, 2),
        },
        {
            method: 'GET', path: '/analytics/errors', title: 'Recent API Errors', description: 'List recent API errors for debugging.', auth: 'X-Admin-Token',
            headers: [{ key: 'X-Admin-Token', value: 'your_admin_token', required: true }],
            queryParams: [{ key: 'hours', type: 'integer', required: false, description: 'Look-back hours (default: 24)' }, { key: 'limit', type: 'integer', required: false, description: 'Max results (default: 50)' }],
            responseCode: 200,
            response: JSON.stringify({ success: true, data: [{ id: 99999, endpoint: "/partner/shipments", response_status: 422, error_code: "VALIDATION_ERROR", api_key_prefix: "sk_live_abc", created_at: "2026-02-13T11:45:00Z" }] }, null, 2),
        },
        // --- System ---
        {
            method: 'GET', path: '/system/health', title: 'System Health', description: 'Check health of all system components (database, cache, NIPOST, queue).', auth: 'X-Admin-Token',
            headers: [{ key: 'X-Admin-Token', value: 'your_admin_token', required: true }],
            responseCode: 200,
            response: JSON.stringify({ success: true, data: { status: "ok", components: { database: { status: "ok" }, cache: { status: "ok" }, nipost_api: { status: "ok", response_time_ms: 230 }, queue: { status: "ok", pending_jobs: 3 } } } }, null, 2),
        },
        {
            method: 'GET', path: '/system/stats', title: 'System Stats', description: 'Get runtime statistics: uptime, memory, disk, database counts.', auth: 'X-Admin-Token',
            headers: [{ key: 'X-Admin-Token', value: 'your_admin_token', required: true }],
            responseCode: 200,
            response: JSON.stringify({ success: true, data: { php_version: "8.2.15", laravel_version: "11.x", uptime: "45 days", memory_usage: "128MB", db_stats: { partners: 25, api_keys: 45, shipments: 2340, api_logs: 145000 } } }, null, 2),
        },
    ],
};

/* ======================================================================
   WEBHOOK EVENTS DATA
   ====================================================================== */

const webhookEvents = [
    { event: "shipment.created", description: "Fired when a new shipment is created", payload: JSON.stringify({ event: "shipment.created", timestamp: "2026-02-13T12:00:00Z", data: { id: 1234, shipment_number: "SKY-20260213-A1B2C3", status: "inReview", sender: { name: "Skynet Lagos HQ", city: "Lagos" }, receiver: { name: "Jane Doe", city: "Abuja" }, weight: 2.5, total_cost: 2500.00 } }, null, 2) },
    { event: "shipment.status_changed", description: "Fired when shipment status changes (e.g., In Transit, Delivered)", payload: JSON.stringify({ event: "shipment.status_changed", timestamp: "2026-02-13T15:00:00Z", data: { id: 1234, shipment_number: "SKY-20260213-A1B2C3", old_status: "inReview", new_status: "In Transit", changed_at: "2026-02-13T15:00:00Z" } }, null, 2) },
    { event: "shipment.delivered", description: "Fired when shipment reaches 'Proof of Delivery' status", payload: JSON.stringify({ event: "shipment.delivered", timestamp: "2026-02-14T10:30:00Z", data: { id: 1234, shipment_number: "SKY-20260213-A1B2C3", status: "Proof of Delivery", delivered_at: "2026-02-14T10:30:00Z", signed_by: "Jane Doe" } }, null, 2) },
    { event: "shipment.cancelled", description: "Fired when a shipment is cancelled", payload: JSON.stringify({ event: "shipment.cancelled", timestamp: "2026-02-13T13:00:00Z", data: { id: 1234, shipment_number: "SKY-20260213-A1B2C3", status: "Cancelled", cancelled_at: "2026-02-13T13:00:00Z", reason: "Customer request" } }, null, 2) },
    { event: "tracking.updated", description: "Fired when new tracking events are received from NIPOST", payload: JSON.stringify({ event: "tracking.updated", timestamp: "2026-02-13T16:00:00Z", data: { id: 1234, shipment_number: "SKY-20260213-A1B2C3", tracking_event: { status: "in_transit", location: "Abuja Sorting Center", timestamp: "2026-02-13T16:00:00Z" } } }, null, 2) },
    { event: "payment.received", description: "Fired when wallet is credited (admin top-up)", payload: JSON.stringify({ event: "payment.received", timestamp: "2026-02-13T09:00:00Z", data: { partner_id: 42, amount: 50000.00, currency: "NGN", new_balance: 100000.00, reference: "ADM-CR-001" } }, null, 2) },
];

/* ======================================================================
   COMPONENT
   ====================================================================== */

export default function PortalDocs() {
    // Which API this account is entitled to see. A partner must never be shown the
    // customer surface or — critically — the Admin API, whose reference explains how
    // admin authentication works.
    const portalAccount = useSelector((state: any) => state.data.login.value);
    const accountType = portalAccount?.data?.account_type === 'customer' ? 'customer' : 'partner';

    const [env, setEnv] = React.useState<'staging' | 'live'>('staging');
    const [activeTab, setActiveTab] = React.useState<string>(accountType);
    const [expandedEndpoints, setExpandedEndpoints] = React.useState<Set<string>>(new Set());
    const [expandedWebhooks, setExpandedWebhooks] = React.useState<Set<string>>(new Set());

    // ── Environment Configuration ──────────────────────────────────────
    const environments = {
        staging: {
            // Section baseUrls already start with /api/v1/…, so the trailing /api on
            // configs.context is stripped — otherwise the URL renders as /api/api/v1/…
            label: 'Staging',
            baseUrl: (configs.context || 'http://127.0.0.1:8000/api').replace(/\/api\/?$/, ''),
            description: 'For development and testing. No real shipments are created.',
            keyPrefix: 'sk_test_',
            adminToken: 'your_staging_admin_token',
            badgeColor: { bg: '#FFF3BF', text: '#E67700', border: '#FFD43B' },
        },
        live: {
            label: 'Live / Production',
            baseUrl: ((configs as any).productionContext || 'https://www.api.skynetworldwide.com.ng').replace(/\/api\/?$/, ''),
            description: 'Production environment. All operations are real and billable.',
            keyPrefix: 'sk_live_',
            adminToken: 'your_production_admin_token',
            badgeColor: { bg: '#D3F9D8', text: '#2B8A3E', border: '#69DB7C' },
        },
    };
    const currentEnv = environments[env];
    const baseUrl = currentEnv.baseUrl;

    // Only this account's own surface. adminSection is deliberately never included —
    // it is internal documentation and belongs on the staff dashboard, not here.
    const sections: Section[] = accountType === 'customer' ? [customerSection] : [partnerSection];
    const activeSection = sections.find(s => s.id === activeTab) || sections[0];

    const toggleEndpoint = (key: string) => {
        setExpandedEndpoints(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const toggleWebhook = (key: string) => {
        setExpandedWebhooks(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        toast.info('Copied to clipboard');
    };

    const methodColor = (m: string) => {
        switch (m) {
            case 'GET': return { bg: '#C9F0D0', text: '#2B8A3E' };
            case 'POST': return { bg: '#FFDDBD', text: '#E8590C' };
            case 'PUT': return { bg: '#D0EBFF', text: '#1971C2' };
            case 'DELETE': return { bg: '#FFD6D6', text: '#C92A2A' };
            default: return { bg: '#E9ECEF', text: '#495057' };
        }
    };

    const expandAll = () => {
        const allKeys = activeSection.endpoints.map((_, i) => `${activeTab}-${i}`);
        setExpandedEndpoints(new Set(allKeys));
    };
    const collapseAll = () => setExpandedEndpoints(new Set());

    return (
        <>
            <PortalSidebar title="API Docs" />
            <div className="relative md:ml-64 bg-[#FAFAFA]">
                <AdminNavbar title="API Documentation" />
                <div className='lg:px-10 pb-6 px-6 bg-[#FAFAFA] min-h-screen'>

                    {/* Header */}
                    <div className="lg:pt-5 pt-3">
                        <h3 className="text-[20px] font-semibold flex items-center gap-2">
                            <FaBook className="text-[#E04343]" /> API Documentation
                        </h3>
                        <p className="text-[13px] font-normal text-[#999999]">
                            Reference for the Skynet Shipping {accountType === 'customer' ? 'Customer' : 'Partner'} API —
                            the endpoints available to your account
                        </p>
                    </div>

                    {/* Environment Switcher */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-5">
                        <div className="flex items-center gap-1 bg-white rounded-[10px] border border-[#E2E8F0] p-1 w-fit">
                            <button onClick={() => setEnv('staging')}
                                className={`flex items-center gap-1.5 px-4 py-2 text-[12px] font-semibold rounded-[7px] transition-all ${env === 'staging' ? 'bg-[#FFF3BF] text-[#E67700] shadow-sm border border-[#FFD43B]' : 'text-[#808080] hover:bg-gray-50'}`}>
                                <FaServer size={11} /> Staging
                            </button>
                            <button onClick={() => setEnv('live')}
                                className={`flex items-center gap-1.5 px-4 py-2 text-[12px] font-semibold rounded-[7px] transition-all ${env === 'live' ? 'bg-[#D3F9D8] text-[#2B8A3E] shadow-sm border border-[#69DB7C]' : 'text-[#808080] hover:bg-gray-50'}`}>
                                <FaRocket size={11} /> Live
                            </button>
                        </div>
                        <div className="flex items-center gap-2">
                            <span style={{ backgroundColor: currentEnv.badgeColor.bg, color: currentEnv.badgeColor.text, border: `1px solid ${currentEnv.badgeColor.border}`, fontSize: '11px', fontWeight: 600, padding: '3px 10px', borderRadius: '20px' }}>
                                {currentEnv.label}
                            </span>
                            <span className="text-[12px] text-[#808080]">{currentEnv.description}</span>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-1 mt-5 bg-white rounded-[10px] border border-[#E2E8F0] p-1.5 w-fit">
                        {sections.map(s => (
                            <button key={s.id} onClick={() => { setActiveTab(s.id); collapseAll(); }}
                                className={`px-5 py-2 text-[13px] font-medium rounded-[7px] transition-all ${activeTab === s.id ? 'bg-[#E04343] text-white shadow-sm' : 'text-[#808080] hover:bg-gray-50'}`}>
                                {s.title}
                            </button>
                        ))}
                        <button onClick={() => { setActiveTab('webhooks'); collapseAll(); }}
                            className={`px-5 py-2 text-[13px] font-medium rounded-[7px] transition-all ${activeTab === 'webhooks' ? 'bg-[#E04343] text-white shadow-sm' : 'text-[#808080] hover:bg-gray-50'}`}>
                            Webhooks
                        </button>
                    </div>

                    {/* Webhooks Tab */}
                    {activeTab === 'webhooks' ? (
                        <div className="mt-5 space-y-4">
                            {/* Webhook overview */}
                            <div className="bg-white rounded-[10px] border border-[#E2E8F0] px-[26px] py-[20px]">
                                <h4 className="text-[16px] font-[600] mb-2">Webhook Events Reference</h4>
                                <p className="text-[13px] text-[#404040] mb-3">
                                    Configure webhooks via the Partner or Customer API (<code className="bg-[#F6F9FF] px-1 text-[12px]">POST /webhooks</code>).
                                    When events occur, Skynet sends a POST request to your registered URL with the payload below.
                                </p>
                                <div className="bg-[#1a1a2e] rounded-[5px] p-3 mb-3">
                                    <p className="text-[11px] text-gray-400 mb-1">All webhook deliveries include these headers:</p>
                                    <code className="text-[12px] text-green-400 font-mono block">Content-Type: application/json</code>
                                    <code className="text-[12px] text-green-400 font-mono block">X-Webhook-Signature: sha256=HMAC_OF_PAYLOAD</code>
                                    <code className="text-[12px] text-green-400 font-mono block">X-Webhook-Event: shipment.created</code>
                                    <code className="text-[12px] text-green-400 font-mono block">X-Webhook-ID: delivery_uuid</code>
                                </div>
                                <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-3">
                                    <p className="text-[12px] text-yellow-800"><strong>Signature Verification:</strong> Compute HMAC-SHA256 of the raw request body using your webhook secret, then compare with the <code>X-Webhook-Signature</code> header. Reject any request where they don't match.</p>
                                </div>
                            </div>

                            {/* Each webhook event */}
                            {webhookEvents.map((ev, idx) => {
                                const key = `wh-${idx}`;
                                const isOpen = expandedWebhooks.has(key);
                                return (
                                    <div key={key} className="bg-white rounded-[10px] border border-[#E2E8F0] overflow-hidden">
                                        <button onClick={() => toggleWebhook(key)}
                                            className="w-full flex items-center justify-between px-[26px] py-[14px] hover:bg-gray-50 transition">
                                            <div className="flex items-center gap-3">
                                                <span className="bg-[#F6F9FF] text-[#4979ED] text-[12px] font-mono px-3 py-1 rounded-full border border-[#D9D9D9]">{ev.event}</span>
                                                <span className="text-[13px] text-[#404040]">{ev.description}</span>
                                            </div>
                                            {isOpen ? <FaChevronDown className="text-gray-400" size={12} /> : <FaChevronRight className="text-gray-400" size={12} />}
                                        </button>
                                        {isOpen && (
                                            <div className="px-[26px] pb-[20px] border-t border-[#E2E8F0]">
                                                <p className="text-[12px] text-[#808080] mt-3 mb-2">Example Payload (POST to your webhook URL):</p>
                                                <div className="bg-[#1a1a2e] rounded-[5px] p-4 overflow-x-auto relative">
                                                    <button onClick={() => copyToClipboard(ev.payload)} className="absolute top-2 right-2 text-gray-500 hover:text-green-400"><FaCopy size={12} /></button>
                                                    <pre className="text-[11px] text-green-400 font-mono whitespace-pre">{ev.payload}</pre>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            {/* Retry policy */}
                            <div className="bg-white rounded-[10px] border border-[#E2E8F0] px-[26px] py-[20px]">
                                <h4 className="text-[16px] font-[600] mb-2">Retry Policy</h4>
                                <p className="text-[13px] text-[#404040]">
                                    If your endpoint returns a non-2xx status, Skynet retries up to <strong>3 times</strong> with exponential backoff (5 min, 30 min, 2 hours).
                                    After 10 consecutive failures, the webhook is auto-disabled. You can re-enable it from the Webhooks page.
                                </p>
                            </div>
                        </div>
                    ) : (
                        /* ========== API Section Tab ========== */
                        <div className="mt-5 space-y-4">
                            {/* Base URL + Auth + Environment */}
                            <div className="bg-white rounded-[10px] border border-[#E2E8F0] px-[26px] py-[20px]">
                                {/* Env banner */}
                                <div className="flex items-center gap-2 mb-4 pb-3 border-b border-[#E2E8F0]">
                                    <span style={{ backgroundColor: currentEnv.badgeColor.bg, color: currentEnv.badgeColor.text, border: `1px solid ${currentEnv.badgeColor.border}`, fontSize: '11px', fontWeight: 700, padding: '3px 12px', borderRadius: '20px' }}>
                                        {env === 'staging' ? '🧪' : '🚀'} {currentEnv.label}
                                    </span>
                                    <span className="text-[12px] text-[#808080]">{currentEnv.description}</span>
                                </div>
                                <div className="flex flex-col md:flex-row md:items-start gap-6">
                                    <div className="flex-1">
                                        <h4 className="text-[14px] font-[600] mb-1.5">Base URL</h4>
                                        <div className="bg-[#1a1a2e] rounded-[5px] p-3 flex justify-between items-center">
                                            <code className="text-[13px] text-green-400 font-mono">{baseUrl}{activeSection.baseUrl}</code>
                                            <button onClick={() => copyToClipboard(`${baseUrl}${activeSection.baseUrl}`)} className="text-gray-400 hover:text-green-400 ml-3"><FaCopy size={12} /></button>
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="text-[14px] font-[600] mb-1.5">Authentication</h4>
                                        <div className="bg-[#1a1a2e] rounded-[5px] p-3">
                                            <code className="text-[13px] text-green-400 font-mono">
                                                {activeSection.authHeader}: {activeSection.id === 'admin' ? currentEnv.adminToken : `${currentEnv.keyPrefix}xxxxxxxxxxxxxxxx`}
                                            </code>
                                        </div>
                                        <p className="text-[11px] text-[#808080] mt-1.5">{activeSection.authDescription}</p>
                                    </div>
                                </div>
                                {env === 'staging' && (
                                    <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-3 mt-4">
                                        <p className="text-[12px] text-yellow-800"><strong>Staging Mode:</strong> API keys prefixed with <code className="bg-yellow-100 px-1">sk_test_</code> work only against the staging server. No real shipments are created and no wallets are debited.</p>
                                    </div>
                                )}
                                {env === 'live' && (
                                    <div className="bg-red-50 border-l-4 border-red-400 rounded-lg p-3 mt-4">
                                        <p className="text-[12px] text-red-800"><strong>Production Mode:</strong> All operations are <strong>real</strong>. API keys prefixed with <code className="bg-red-100 px-1">sk_live_</code> create real shipments and debit real wallets. Test thoroughly in staging first.</p>
                                    </div>
                                )}
                            </div>

                            {/* Expand/Collapse controls */}
                            <div className="flex items-center justify-between">
                                <h4 className="text-[15px] font-[600] text-[#263238]">{activeSection.endpoints.length} Endpoints</h4>
                                <div className="flex gap-2">
                                    <button onClick={expandAll} className="text-[12px] text-[#E04343] hover:underline">Expand All</button>
                                    <span className="text-[12px] text-[#DADADA]">|</span>
                                    <button onClick={collapseAll} className="text-[12px] text-[#808080] hover:underline">Collapse All</button>
                                </div>
                            </div>

                            {/* Endpoints */}
                            {activeSection.endpoints.map((ep, idx) => {
                                const key = `${activeTab}-${idx}`;
                                const isOpen = expandedEndpoints.has(key);
                                const mc = methodColor(ep.method);
                                return (
                                    <div key={key} className="bg-white rounded-[10px] border border-[#E2E8F0] overflow-hidden">
                                        {/* Header row */}
                                        <button onClick={() => toggleEndpoint(key)}
                                            className="w-full flex items-center justify-between px-[20px] py-[12px] hover:bg-gray-50 transition text-left">
                                            <div className="flex items-center gap-3 flex-wrap">
                                                <span style={{ backgroundColor: mc.bg, color: mc.text, fontWeight: 600, fontSize: '11px', borderRadius: '4px', padding: '3px 10px', minWidth: '56px', textAlign: 'center' }}>{ep.method}</span>
                                                <code className="text-[13px] font-mono text-[#263238]">{ep.path}</code>
                                                <span className="text-[12px] text-[#808080] hidden md:inline">— {ep.title}</span>
                                            </div>
                                            {isOpen ? <FaChevronDown className="text-gray-400 flex-shrink-0" size={12} /> : <FaChevronRight className="text-gray-400 flex-shrink-0" size={12} />}
                                        </button>

                                        {/* Expanded detail */}
                                        {isOpen && (
                                            <div className="px-[20px] pb-[20px] border-t border-[#E2E8F0] pt-4 space-y-4">
                                                <div>
                                                    <h5 className="text-[14px] font-[600] text-[#263238] mb-1">{ep.title}</h5>
                                                    <p className="text-[13px] text-[#404040]">{ep.description}</p>
                                                </div>

                                                {/* Full URL */}
                                                <div>
                                                    <p className="text-[11px] font-semibold text-[#808080] uppercase mb-1">URL</p>
                                                    <div className="bg-[#1a1a2e] rounded-[5px] p-2.5 flex justify-between items-center">
                                                        <code className="text-[12px] text-green-400 font-mono">
                                                            <span className="text-yellow-300">{ep.method}</span> {baseUrl}{activeSection.baseUrl}{ep.path}
                                                        </code>
                                                        <button onClick={() => copyToClipboard(`${baseUrl}${activeSection.baseUrl}${ep.path}`)} className="text-gray-500 hover:text-green-400 ml-2"><FaCopy size={11} /></button>
                                                    </div>
                                                </div>

                                                {/* Headers */}
                                                <div>
                                                    <p className="text-[11px] font-semibold text-[#808080] uppercase mb-1">Headers</p>
                                                    <div className="bg-[#F8F9FA] rounded-[5px] border border-[#E2E8F0] overflow-hidden">
                                                        <table className="w-full text-[12px]">
                                                            <thead><tr className="bg-[#F1F3F5]">
                                                                <th className="text-left px-3 py-1.5 font-semibold text-[#808080]">Header</th>
                                                                <th className="text-left px-3 py-1.5 font-semibold text-[#808080]">Value</th>
                                                                <th className="text-left px-3 py-1.5 font-semibold text-[#808080]">Required</th>
                                                            </tr></thead>
                                                            <tbody>
                                                                {ep.headers.map((h, hi) => {
                                                                    // Replace static key examples with env-aware prefix
                                                                    let displayValue = h.value;
                                                                    if (h.key === 'X-API-Key') {
                                                                        displayValue = `${currentEnv.keyPrefix}xxxxxxxxxxxxxxxx`;
                                                                    } else if (h.key === 'X-Admin-Token') {
                                                                        displayValue = currentEnv.adminToken;
                                                                    }
                                                                    return (
                                                                        <tr key={hi} className="border-t border-[#E2E8F0]">
                                                                            <td className="px-3 py-1.5 font-mono text-[#263238]">{h.key}</td>
                                                                            <td className="px-3 py-1.5 font-mono text-[#495057]">{displayValue}</td>
                                                                            <td className="px-3 py-1.5">{h.required ? <span className="text-red-500 font-semibold">Yes</span> : <span className="text-[#808080]">No</span>}</td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>

                                                {/* Query params */}
                                                {ep.queryParams && ep.queryParams.length > 0 && (
                                                    <div>
                                                        <p className="text-[11px] font-semibold text-[#808080] uppercase mb-1">Query Parameters</p>
                                                        <div className="bg-[#F8F9FA] rounded-[5px] border border-[#E2E8F0] overflow-hidden">
                                                            <table className="w-full text-[12px]">
                                                                <thead><tr className="bg-[#F1F3F5]">
                                                                    <th className="text-left px-3 py-1.5 font-semibold text-[#808080]">Param</th>
                                                                    <th className="text-left px-3 py-1.5 font-semibold text-[#808080]">Type</th>
                                                                    <th className="text-left px-3 py-1.5 font-semibold text-[#808080]">Required</th>
                                                                    <th className="text-left px-3 py-1.5 font-semibold text-[#808080]">Description</th>
                                                                </tr></thead>
                                                                <tbody>
                                                                    {ep.queryParams.map((qp, qi) => (
                                                                        <tr key={qi} className="border-t border-[#E2E8F0]">
                                                                            <td className="px-3 py-1.5 font-mono text-[#263238]">{qp.key}</td>
                                                                            <td className="px-3 py-1.5 text-[#495057]">{qp.type}</td>
                                                                            <td className="px-3 py-1.5">{qp.required ? <span className="text-red-500 font-semibold">Yes</span> : <span className="text-[#808080]">No</span>}</td>
                                                                            <td className="px-3 py-1.5 text-[#404040]">{qp.description}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Request body */}
                                                {ep.body && (
                                                    <div>
                                                        <p className="text-[11px] font-semibold text-[#808080] uppercase mb-1">Request Body</p>
                                                        <div className="bg-[#1a1a2e] rounded-[5px] p-3.5 overflow-x-auto relative">
                                                            <button onClick={() => copyToClipboard(ep.body!)} className="absolute top-2 right-2 text-gray-500 hover:text-green-400"><FaCopy size={11} /></button>
                                                            <pre className="text-[11px] text-green-400 font-mono whitespace-pre">{ep.body}</pre>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Response */}
                                                <div>
                                                    <p className="text-[11px] font-semibold text-[#808080] uppercase mb-1">
                                                        Response <span className={`ml-2 px-2 py-0.5 rounded text-[10px] font-bold ${ep.responseCode < 300 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{ep.responseCode}</span>
                                                    </p>
                                                    <div className="bg-[#1a1a2e] rounded-[5px] p-3.5 overflow-x-auto relative">
                                                        <button onClick={() => copyToClipboard(ep.response)} className="absolute top-2 right-2 text-gray-500 hover:text-green-400"><FaCopy size={11} /></button>
                                                        <pre className="text-[11px] text-[#60a5fa] font-mono whitespace-pre">{ep.response}</pre>
                                                    </div>
                                                </div>

                                                {/* Error codes */}
                                                {ep.errorCodes && ep.errorCodes.length > 0 && (
                                                    <div>
                                                        <p className="text-[11px] font-semibold text-[#808080] uppercase mb-1">Error Codes</p>
                                                        <div className="bg-[#F8F9FA] rounded-[5px] border border-[#E2E8F0] overflow-hidden">
                                                            <table className="w-full text-[12px]">
                                                                <thead><tr className="bg-[#F1F3F5]">
                                                                    <th className="text-left px-3 py-1.5 font-semibold text-[#808080]">Code</th>
                                                                    <th className="text-left px-3 py-1.5 font-semibold text-[#808080]">HTTP</th>
                                                                    <th className="text-left px-3 py-1.5 font-semibold text-[#808080]">Description</th>
                                                                </tr></thead>
                                                                <tbody>
                                                                    {ep.errorCodes.map((ec, ei) => (
                                                                        <tr key={ei} className="border-t border-[#E2E8F0]">
                                                                            <td className="px-3 py-1.5 font-mono text-red-600">{ec.code}</td>
                                                                            <td className="px-3 py-1.5 text-[#263238]">{ec.status}</td>
                                                                            <td className="px-3 py-1.5 text-[#404040]">{ec.description}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Global info footer */}
                    <div className="mt-6 space-y-4">
                        <div className="bg-white rounded-[10px] border border-[#E2E8F0] px-[26px] py-[20px]">
                            <h4 className="text-[16px] font-[600] mb-2">Rate Limits</h4>
                            <p className="text-[13px] text-[#404040]">
                                Default: <strong>120 requests/min</strong> (partner), <strong>60 requests/min</strong> (customer). HTTP <code className="bg-[#F6F9FF] px-1 text-[12px]">429</code> is returned when exceeded.
                            </p>
                            <p className="text-[13px] text-[#404040] mt-2">
                                Rate limit headers included in every response:
                                <code className="bg-[#F6F9FF] px-1 text-[12px] ml-1">X-RateLimit-Limit</code>,
                                <code className="bg-[#F6F9FF] px-1 text-[12px] ml-1">X-RateLimit-Remaining</code>,
                                <code className="bg-[#F6F9FF] px-1 text-[12px] ml-1">X-RateLimit-Reset</code>
                            </p>
                        </div>

                        <div className="bg-white rounded-[10px] border border-[#E2E8F0] px-[26px] py-[20px]">
                            <h4 className="text-[16px] font-[600] mb-2">Error Response Format</h4>
                            <p className="text-[13px] text-[#404040] mb-3">All errors follow this standard structure:</p>
                            <div className="bg-[#1a1a2e] rounded-[5px] p-3.5 overflow-x-auto">
                                <pre className="text-[11px] text-[#ff6b6b] font-mono whitespace-pre">{JSON.stringify({
                                    success: false,
                                    message: "Human-readable error message",
                                    error: { code: "ERROR_CODE" }
                                }, null, 2)}</pre>
                            </div>
                        </div>

                        <div className="bg-white rounded-[10px] border border-[#E2E8F0] px-[26px] py-[20px]">
                            <h4 className="text-[16px] font-[600] mb-2">Common HTTP Status Codes</h4>
                            <div className="bg-[#F8F9FA] rounded-[5px] border border-[#E2E8F0] overflow-hidden">
                                <table className="w-full text-[12px]">
                                    <thead><tr className="bg-[#F1F3F5]">
                                        <th className="text-left px-3 py-1.5 font-semibold text-[#808080]">Status</th>
                                        <th className="text-left px-3 py-1.5 font-semibold text-[#808080]">Meaning</th>
                                    </tr></thead>
                                    <tbody>
                                        {[
                                            { code: '200', desc: 'Success' },
                                            { code: '201', desc: 'Created — resource created successfully' },
                                            { code: '401', desc: 'Unauthorized — missing or invalid API key' },
                                            { code: '402', desc: 'Payment Required — insufficient wallet balance' },
                                            { code: '403', desc: 'Forbidden — valid key but insufficient permissions' },
                                            { code: '404', desc: 'Not Found — resource does not exist' },
                                            { code: '409', desc: 'Conflict — action not allowed in current state' },
                                            { code: '422', desc: 'Validation Error — check request body fields' },
                                            { code: '429', desc: 'Rate Limit Exceeded — slow down requests' },
                                            { code: '500', desc: 'Server Error — internal failure (report to admin)' },
                                            { code: '502', desc: 'Bad Gateway — upstream NIPOST API error' },
                                        ].map((s, i) => (
                                            <tr key={i} className="border-t border-[#E2E8F0]">
                                                <td className="px-3 py-1.5 font-mono font-semibold text-[#263238]">{s.code}</td>
                                                <td className="px-3 py-1.5 text-[#404040]">{s.desc}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
            <ToastContainer position="top-right" autoClose={2000} hideProgressBar={true} />
        </>
    );
}